import { useCallback, useMemo } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useLocation, useNavigate } from 'react-router-dom'
import { MarketoBillLeadInput } from '_gql'

import { useIntl } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { useAuth } from '@sputnik/auth'
import { getAccountingSoftwareItem } from '@sputnik/components/AccountingSoftwareDropdown'
import { getMarketoValues } from '@sputnik/marketo'
import { GetCreditApplicationsDocument } from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/GetCreditApplications.gql'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { type InitCreditApplication } from '@sputnik/schema/creditApplication/init'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError, logInfo } from '@sputnik/utils/loggerUtils'
import { getClientTimezone } from '@sputnik/utils/timezoneUtil'

import {
  InitCreditApplicationV3_initCreditApplication,
  InitCreditApplicationV3_initCreditApplication_CreditApplication,
  InitCreditApplicationV3Variables,
  useInitCreditApplicationV3,
} from './InitCreditApplicationV3.gql'

const isResponseCreditApplicationType = (
  response: InitCreditApplicationV3_initCreditApplication,
): response is InitCreditApplicationV3_initCreditApplication_CreditApplication => {
  return response.__typename === 'CreditApplication'
}

const useGetQueryParamsForCreditInit = () => {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const { requestApplicantToken } = useAuth()
  const showToast = useToast()
  const { search, pathname } = useLocation()
  const { tracker } = useWebTracking()

  const applicantData = useMemo(() => {
    try {
      if (!search) {
        return null
      }
      // we don't actually send well formed query params for all this applicant data,
      // so we cannot actually rely on normal query param parsing to grab the applicant data
      // that is sent.
      const hasActualQueryParameters = search.indexOf('&') > 0

      const decodedSearchArray = JSON.parse(
        atob(search.slice(1, hasActualQueryParameters ? search.indexOf('&') : search.length)),
      )

      const applicantDataInSearchArray = decodedSearchArray.find((item) => item?.applicantData)
      return applicantDataInSearchArray?.applicantData || null
    } catch {
      return null
    }
  }, [search])

  const dataFromUp = useMemo(() => {
    try {
      if (!search) {
        return {}
      }
      /* we don't actually send well formed query params for all this applicant data,
            so we cannot actually rely on normal query param parsing to grab the applicant data
            that is sent. */
      const hasActualQueryParameters = search.indexOf('&') > 0

      const items = JSON.parse(atob(search.slice(1, hasActualQueryParameters ? search?.indexOf('&') : search?.length)))
      const email = items?.find((item: any) => item?.email)?.email || ''
      const ltLandingPage = items?.find((item: any) => item?.lt)?.lt?.landingPage || ''

      return { ...items, email, ltLandingPage }
    } catch {
      return {}
    }
  }, [search])

  const [initCreditAppMutation, { loading: isLoadingInitCreditApplication }] = useInitCreditApplicationV3({
    onCompleted: (data) => {
      if (isResponseCreditApplicationType(data.initCreditApplication)) {
        const { salesforceCreditId } = data.initCreditApplication ?? {}

        logInfo('initCreditApplication', {
          attributes: {
            creditAppId: salesforceCreditId,
            result: `Credit application initialized from ${pathname}`,
          },
          eventName: 'initCreditApplicationSuccess',
        })
        tracker?.track('se_signup__has__started')
        const handleInitCreditAppComplete = async (salesforceCreditId: string) => {
          await requestApplicantToken(salesforceCreditId)
          navigate(
            {
              pathname: `/app/${salesforceCreditId}/${PATHNAME_CONSTS.NAME_AND_OWNERSHIP_PATH}`,
            },
            { replace: true },
          )
        }
        handleInitCreditAppComplete(salesforceCreditId)
      }
    },
    onError: (error) => {
      showToast(
        TOAST_TYPE_DANGER,
        formatMessage({
          defaultMessage: 'Error while initializing credit application.',
          id: 'sputnik.useGetQueryParamsForCreditInit__V2ViaC',
        }),
        {
          className: 'fs-unmask',
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'initCreditApplication',
          result: error,
          message: 'Error while initializing credit application.',
        },
        eventName: 'initCreditApplicationError',
      })
    },
    // we are refetching here because we do not know where it will end up in the paginated
    // list of results, and we do not return the paginated list from this mutation.
    refetchQueries: [{ query: GetCreditApplicationsDocument, variables: { first: 20 } }],
  })

  const { executeRecaptcha } = useGoogleReCaptcha()

  const verifyReCaptcha = useCallback(
    async (action: string) => {
      if (!executeRecaptcha || !window.__DivvyEnvironment.GOOGLE_RECAPTCHA_SITE_KEY) {
        return ''
      }

      const token = await executeRecaptcha(action)

      logInfo(action, {
        attributes: {
          result: 'Token retrieved',
        },
        eventName: 'ReCaptcha',
      })

      return token
    },
    [executeRecaptcha],
  )

  const handleInitCreditApplication = useCallback(
    async (values: InitCreditApplication & { companyName?: string }) => {
      const billMarketoLeadParams = {
        // we strip values off of what our generated type defines,
        // so casting this kind of makes sense to avoid breaking our lead tracking
        // functionality.
        marketoBillLeadInput: getMarketoValues(dataFromUp?.ltLandingPage) as MarketoBillLeadInput,
      }

      const leadParams = billMarketoLeadParams?.marketoBillLeadInput
      const pact = leadParams?.pact
      const affiliateIdString = leadParams?.ltAffiliateId
      const affiliateId = (affiliateIdString && Number.parseInt(affiliateIdString)) || null
      const afidField = affiliateId ? { affiliateId } : {}
      const pactField = pact ? { partnerCode: pact } : {}

      const {
        email,
        firstName,
        lastName,
        legalBusinessName,
        companyName,
        numberOfEmployees,
        phoneNumber,
        accountingSoftware: accountingSoftwareValue,
        title,
      } = values
      const recaptchaToken = await verifyReCaptcha('initApplication')

      const mutationVariables: InitCreditApplicationV3Variables = {
        applicant: {
          email,
          firstName,
          lastName,
          phoneNumber,
          title: title || '',
        },
        businessInfo: {
          legalBusinessName: legalBusinessName || companyName,
          numberOfEmployees: Number(numberOfEmployees),
          accountingSoftware: getAccountingSoftwareItem(accountingSoftwareValue ?? ''),
        },
        recaptchaToken,
        ...afidField,
        ...pactField,
        ...billMarketoLeadParams,
        appVersion: 3,
        clientTimeZone: getClientTimezone() as string,
      }
      initCreditAppMutation({ variables: mutationVariables })
    },
    [dataFromUp?.ltLandingPage, initCreditAppMutation, verifyReCaptcha],
  )

  const hasRequiredKeys = (applicantData) => {
    if (!applicantData) return false
    // we have to check for legalBusinessName or companyName because somewhere along the way, they changed the key name, so some both are coming in from various places.
    const businessName = applicantData.legalBusinessName || applicantData.companyName
    const requiredKeys = ['email', 'firstName', 'lastName', 'numberOfEmployees', 'phone']

    const hasValue = (value) => {
      return value !== undefined && value !== null && !(typeof value === 'string' && value.trim() === '')
    }

    const hasAllRequiredKeys = requiredKeys.every((key) => {
      const value = applicantData[key]
      return hasValue(value)
    })

    return hasAllRequiredKeys && hasValue(businessName)
  }

  const shouldNavigateToGetToKnowYou = !search || !hasRequiredKeys(applicantData)

  return {
    shouldNavigateToGetToKnowYou,
    handleInitCreditApplication,
    isLoadingInitCreditApplication,
    applicantData,
    dataFromUp,
  }
}

export default useGetQueryParamsForCreditInit
